/**
 * useWasteAssociationStatesLayer - Hook para visualizar estados de asociación Waste en el mapa
 *
 * @remarks
 * MVP: Agrega una capa al mapa que muestra entidades Waste con bordes coloreados
 * según su estado de asociación (OK=verde, REVISAR=amarillo, ERROR=rojo).
 *
 * Este hook debe ser llamado desde un componente que tenga acceso al mapa.
 *
 * @see WASTE_ASSOCIATIONS_MVP_IMPLEMENTATION.md
 */

import { useEffect, useRef, useCallback } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { mapEngine } from '../../../map-engine/MapEngineService';
import { useProject } from '../../../projects';
import { wasteAssociationApi } from '../../../spatial/services/point-association-api.service';

// Layer and source IDs
const SOURCE_ID = 'waste-association-states';
const BORDER_LAYER_ID = 'waste-association-states-border';
const CIRCLE_LAYER_ID = 'waste-association-states-circle';

// Colors for simple states
const STATE_COLORS = {
  OK: '#22c55e',      // green-500
  REVISAR: '#eab308', // yellow-500
  ERROR: '#ef4444',   // red-500
  default: '#6b7280', // gray-500
};

// Colors for entity types (inner circle)
const ENTITY_TYPE_COLORS = {
  waste_container: '#3b82f6', // blue
  waste_depot: '#8b5cf6',     // purple
  waste_hotspot: '#f97316',   // orange
  default: '#6b7280',         // gray
};

export interface UseWasteAssociationStatesLayerOptions {
  /** Whether the layer should be visible */
  visible: boolean;
  /** Callback when layer data is refreshed */
  onRefresh?: () => void;
  /** Currently selected entity ID for highlight */
  selectedEntityId?: string | null;
}

export interface UseWasteAssociationStatesLayerReturn {
  /** Manually refresh the layer data */
  refresh: () => Promise<void>;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
}

/**
 * Hook that adds and manages the waste association states layer on the map
 */
export function useWasteAssociationStatesLayer({
  visible,
  onRefresh,
  selectedEntityId,
}: UseWasteAssociationStatesLayerOptions): UseWasteAssociationStatesLayerReturn {
  const { projectId } = useProject();
  const layersCreatedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const errorRef = useRef<Error | null>(null);
  const previousSelectedRef = useRef<string | null>(null);

  // Setup layers on mount
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const setupLayers = (): void => {
      if (layersCreatedRef.current) return;
      layersCreatedRef.current = true;

      // 1. Create source if not exists (with promoteId for feature-state)
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
          promoteId: 'entityId', // Use entityId for feature-state
        });
      }

      // 2. Border layer (shows state color, larger when selected)
      if (!map.getLayer(BORDER_LAYER_ID)) {
        map.addLayer({
          id: BORDER_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          paint: {
            // Larger radius when selected (uses feature-state)
            'circle-radius': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              14, // selected
              10, // normal
            ],
            'circle-color': 'transparent',
            // Thicker stroke when selected
            'circle-stroke-width': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              5, // selected
              3, // normal
            ],
            'circle-stroke-color': [
              'match',
              ['get', 'simpleState'],
              'OK', STATE_COLORS.OK,
              'REVISAR', STATE_COLORS.REVISAR,
              'ERROR', STATE_COLORS.ERROR,
              STATE_COLORS.default,
            ],
          },
          layout: {
            visibility: visible ? 'visible' : 'none',
          },
        });
      }

      // 3. Inner circle layer (shows entity type color)
      if (!map.getLayer(CIRCLE_LAYER_ID)) {
        map.addLayer({
          id: CIRCLE_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          paint: {
            'circle-radius': 6,
            'circle-color': [
              'match',
              ['get', 'entityType'],
              'waste_container', ENTITY_TYPE_COLORS.waste_container,
              'waste_depot', ENTITY_TYPE_COLORS.waste_depot,
              'waste_hotspot', ENTITY_TYPE_COLORS.waste_hotspot,
              ENTITY_TYPE_COLORS.default,
            ],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
          },
          layout: {
            visibility: visible ? 'visible' : 'none',
          },
        });
      }
    };

    if (map.isStyleLoaded()) {
      setupLayers();
    } else {
      map.once('idle', setupLayers);
    }

    // Cleanup
    return () => {
      const currentMap = mapEngine.getMap();
      if (currentMap) {
        if (currentMap.getLayer(CIRCLE_LAYER_ID)) {
          currentMap.removeLayer(CIRCLE_LAYER_ID);
        }
        if (currentMap.getLayer(BORDER_LAYER_ID)) {
          currentMap.removeLayer(BORDER_LAYER_ID);
        }
        if (currentMap.getSource(SOURCE_ID)) {
          currentMap.removeSource(SOURCE_ID);
        }
        layersCreatedRef.current = false;
      }
    };
  }, [visible]);

  // Update visibility when it changes
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersCreatedRef.current) return;

    const visibility = visible ? 'visible' : 'none';

    if (map.getLayer(BORDER_LAYER_ID)) {
      map.setLayoutProperty(BORDER_LAYER_ID, 'visibility', visibility);
    }
    if (map.getLayer(CIRCLE_LAYER_ID)) {
      map.setLayoutProperty(CIRCLE_LAYER_ID, 'visibility', visibility);
    }
  }, [visible]);

  // Fetch and update data
  const fetchData = useCallback(async () => {
    if (!projectId || !visible) return;

    const map = mapEngine.getMap();
    if (!map) return;

    const source = map.getSource(SOURCE_ID) as GeoJSONSource;
    if (!source) return;

    isLoadingRef.current = true;
    errorRef.current = null;

    try {
      const data = await wasteAssociationApi.getEntitiesWithStates(projectId);
      source.setData(data);
      onRefresh?.();
    } catch (err) {
      console.error('[useWasteAssociationStatesLayer] Error fetching data:', err);
      errorRef.current = err instanceof Error ? err : new Error('Failed to fetch data');
      // Clear the source on error
      source.setData({
        type: 'FeatureCollection',
        features: [],
      });
    } finally {
      isLoadingRef.current = false;
    }
  }, [projectId, visible, onRefresh]);

  // Fetch data when projectId changes or visibility turns on
  useEffect(() => {
    if (visible && projectId) {
      fetchData();
    }
  }, [visible, projectId, fetchData]);

  // Update selection highlight when selectedEntityId changes
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersCreatedRef.current) return;

    // Clear previous selection
    if (previousSelectedRef.current && previousSelectedRef.current !== selectedEntityId) {
      try {
        map.setFeatureState(
          { source: SOURCE_ID, id: previousSelectedRef.current },
          { selected: false }
        );
      } catch {
        // Feature might not exist
      }
    }

    // Set new selection
    if (selectedEntityId) {
      try {
        map.setFeatureState(
          { source: SOURCE_ID, id: selectedEntityId },
          { selected: true }
        );
      } catch {
        // Feature might not exist
      }
    }

    previousSelectedRef.current = selectedEntityId || null;
  }, [selectedEntityId]);

  return {
    refresh: fetchData,
    isLoading: isLoadingRef.current,
    error: errorRef.current,
  };
}

export default useWasteAssociationStatesLayer;
